import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import '@theme/product-form';

/** Presentation and request feedback only; Horizon owns all cart requests and events. */
class PatternFavorites extends Component {
  #controller;
  #observer;
  #pending = new Map();
  #timers = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  updatedCallback() {
    this.#disconnect();
    super.updatedCallback();
    this.#connect();
  }

  disconnectedCallback() {
    this.#disconnect();
    super.disconnectedCallback();
  }

  #connect() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    reduced.addEventListener('change', this.#reveal, { signal });
    this.#reveal();
  }

  #disconnect() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    for (const [form, state] of this.#pending) {
      state.button.disabled = state.disabled;
      state.label.textContent = state.original;
      form.removeAttribute('aria-busy');
    }
    this.#pending.clear();
    for (const [label, state] of this.#timers) {
      clearTimeout(state.timer);
      label.textContent = state.original;
    }
    this.#timers.clear();
    this.classList.remove('is-js');
  }

  #reveal = () => {
    this.#observer?.disconnect();
    const elements = this.querySelectorAll('[data-tb-reveal]');
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      for (const element of elements) element.classList.add('is-in');
      return;
    }
    this.#observer = new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
    for (const element of elements) this.#observer.observe(element);
    this.classList.add('is-js');
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #onCartUpdate = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLElement) || !form.matches('product-form-component') || event.action !== 'add' || !event.promise) return;
    const button = form.querySelector('[ref="addToCartButton"]');
    const label = button?.querySelector('[data-tb-button-label]');
    if (!(button instanceof HTMLButtonElement) || !(label instanceof HTMLElement)) return;
    const previous = this.#timers.get(label);
    if (previous) {
      clearTimeout(previous.timer);
      this.#timers.delete(label);
    }
    const state = { button, label, original: previous?.original ?? label.textContent, disabled: button.disabled };
    const { signal } = this.#controller;
    this.#pending.set(form, state);
    button.disabled = true;
    form.setAttribute('aria-busy', 'true');
    label.textContent = this.dataset.addingLabel || '';
    try {
      const { detail } = await event.promise;
      if (signal.aborted) return;
      label.textContent = (detail?.didError ? this.dataset.errorLabel : this.dataset.addedLabel) || state.original;
    } catch {
      if (signal.aborted) return;
      label.textContent = this.dataset.errorLabel || state.original;
      // Native product-form owns server errors; rejected network promises also need visible feedback.
      const error = form.querySelector('[ref="addToCartTextError"]');
      if (error) {
        const message = this.dataset.networkError || '';
        if (error.childNodes[2]) error.childNodes[2].textContent = message;
        else error.appendChild(document.createTextNode(message));
        error.classList.remove('hidden');
      }
      const liveRegion = form.querySelector('[ref="liveRegion"]');
      if (liveRegion) liveRegion.textContent = this.dataset.networkError || '';
    } finally {
      if (!signal.aborted) {
        button.disabled = state.disabled;
        form.removeAttribute('aria-busy');
        this.#pending.delete(form);
        const timer = window.setTimeout(() => {
          label.textContent = state.original;
          this.#timers.delete(label);
        }, 1600);
        this.#timers.set(label, { timer, original: state.original });
      }
    }
  };
}

if (!customElements.get('tb-favorites-component')) customElements.define('tb-favorites-component', PatternFavorites);
